import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { createPart, fetchPart, updatePart } from '@/lib/parts';
import { uploadPartPhoto, deletePartPhoto } from '@/lib/storage';
import { NON_ALL_CATEGORIES } from '@/data/categories';
import { type PartIconId, PartIcon } from '@/icons/parts';
import IconPicker from '@/components/admin/IconPicker';
import type { PartDraft, PartPhoto, PartType, PartCondition, PartStatus } from '@/types/Part';
import { useAuth } from '@/hooks/useAuth';
import AdminLayout from '@/components/admin/AdminLayout';

const TYPES: PartType[] = ['OEM', 'Reproduction'];
const CONDITIONS: PartCondition[] = ['NOS', 'Excellent', 'Good', 'Restored', 'Restorable', 'Core'];
const STATUSES: PartStatus[] = ['live', 'reserved', 'sold', 'draft'];

interface FormState {
  id: string;
  sku: string;
  title: string;
  year: string;
  category: string;
  iconId: PartIconId;
  type: PartType;
  condition: PartCondition;
  price: string;
  weight: string;
  casting: string;
  dateCode: string;
  fitment: string;
  description: string;
  verifiedBy: string;
  verifiedDate: string;
  authNotes: string;
  status: PartStatus;
  stock: string;
}

/** 20-char Firestore-style auto-id. */
function newPartId(): string {
  const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 20; i++) {
    id += alpha[Math.floor(Math.random() * alpha.length)];
  }
  return id;
}

function emptyForm(defaultVerifier: string): FormState {
  return {
    id: newPartId(),
    sku: '',
    title: '',
    year: String(new Date().getFullYear() - 56),
    category: 'engine-block',
    iconId: 'i-block',
    type: 'OEM',
    condition: 'Excellent',
    price: '',
    weight: '',
    casting: '',
    dateCode: '',
    fitment: '',
    description: '',
    verifiedBy: defaultVerifier,
    verifiedDate: new Date().toISOString().slice(0, 10),
    authNotes: '',
    status: 'draft',
    stock: '1',
  };
}

export default function PartEditor() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(() => emptyForm(user?.displayName ?? 'Admin'));
  const [photos, setPhotos] = useState<PartPhoto[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    fetchPart(id).then((p) => {
      if (!p) {
        setError(`Part ${id} not found.`);
        setLoading(false);
        return;
      }
      setForm({
        id: p.id,
        sku: p.sku,
        title: p.title,
        year: String(p.year),
        category: p.category,
        iconId: p.iconId as PartIconId,
        type: p.type,
        condition: p.condition,
        price: String(p.price),
        weight: String(p.weight),
        casting: p.casting ?? '',
        dateCode: p.dateCode ?? '',
        fitment: p.fitment.join('\n'),
        description: p.description,
        verifiedBy: p.authentication.verifiedBy,
        verifiedDate: p.authentication.verifiedDate?.toDate
          ? p.authentication.verifiedDate.toDate().toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10),
        authNotes: p.authentication.notes,
        status: p.status,
        stock: String(p.stock),
      });
      setPhotos(p.photos ?? []);
      setLoading(false);
    });
  }, [isEdit, id]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function buildDraft(): PartDraft | null {
    const year = parseInt(form.year, 10);
    const price = parseFloat(form.price);
    const weight = parseFloat(form.weight);
    const stock = parseInt(form.stock, 10);
    if (!form.sku.trim() || !form.title.trim()) {
      setError('SKU and Title are required.');
      return null;
    }
    if (Number.isNaN(year) || Number.isNaN(price) || Number.isNaN(weight) || Number.isNaN(stock)) {
      setError('Year, price, weight, and stock must be numbers.');
      return null;
    }
    return {
      id: form.id.trim(),
      sku: form.sku.trim(),
      title: form.title.trim(),
      year,
      category: form.category,
      iconId: form.iconId,
      type: form.type,
      condition: form.condition,
      price,
      weight,
      casting: form.casting.trim() || undefined,
      dateCode: form.dateCode.trim() || undefined,
      // One vehicle per line — split on newlines or pipes (legacy data).
      fitment: form.fitment
        .split(/[\n|]/)
        .map((s) => s.trim())
        .filter(Boolean),
      description: form.description.trim(),
      photos,
      authentication: {
        verifiedBy: form.verifiedBy.trim(),
        verifiedDate: form.verifiedDate,
        notes: form.authNotes.trim(),
      },
      status: form.status,
      stock,
    };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const draft = buildDraft();
    if (!draft) return;
    setSaving(true);
    try {
      if (isEdit && id) {
        await updatePart(id, draft);
        toast.success('Part saved', { description: draft.title });
      } else {
        await createPart(draft);
        toast.success('Part created', { description: draft.title });
      }
      navigate('/admin/inventory');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setError(msg);
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoUpload(file: File) {
    setUploading(true);
    setError(null);
    try {
      const photo = await uploadPartPhoto(form.id, file, form.title || form.sku, photos.length);
      setPhotos((p) => [...p, photo]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      toast.error('Upload failed', { description: msg });
    } finally {
      setUploading(false);
    }
  }

  async function handleRemovePhoto(photo: PartPhoto) {
    setPhotos((p) => p.filter((x) => x.storagePath !== photo.storagePath));
    await deletePartPhoto(photo.storagePath);
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="empty-state">Loading part…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="row between" style={{ marginBottom: 16 }}>
        <div>
          <div className="eyebrow">{isEdit ? 'Edit Part' : 'New Part'}</div>
          <h1 className="h-title" style={{ margin: 0 }}>{isEdit ? form.title || form.sku : 'Add a Part'}</h1>
        </div>
        <button type="button" className="btn dark sm" onClick={() => navigate('/admin/inventory')}>← Back</button>
      </div>

      {error && <div className="auth-error" style={{ marginBottom: 18 }}>{error}</div>}

      <form onSubmit={handleSubmit} className="part-form">
        <div className="part-form-grid">

          {/* ---------- Identity ---------- */}
          <div className="card-elev">
            <h2 className="h-section">Identity</h2>
            <label className="auth-field">
              <span className="auth-label">SKU</span>
              <input
                value={form.sku}
                onChange={(e) => update('sku', e.target.value)}
                placeholder="SWC-HEMI-INTAKE-CR"
                required
              />
              <small className="field-help">Your internal part number. Use a pattern that means something to you.</small>
            </label>
            <label className="auth-field">
              <span className="auth-label">Title</span>
              <input
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="1970 426 Hemi Cross Ram Intake Manifold"
                required
              />
              <small className="field-help">What customers see at the top of the listing.</small>
            </label>
            <div className="form-row">
              <label className="auth-field">
                <span className="auth-label">Year</span>
                <input type="number" min="1900" max="2100" value={form.year} onChange={(e) => update('year', e.target.value)} required />
              </label>
              <label className="auth-field">
                <span className="auth-label">Category</span>
                <select value={form.category} onChange={(e) => update('category', e.target.value)}>
                  {NON_ALL_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="auth-field">
                <span className="auth-label">Type</span>
                <select value={form.type} onChange={(e) => update('type', e.target.value as PartType)}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="auth-field">
                <span className="auth-label">Condition</span>
                <select value={form.condition} onChange={(e) => update('condition', e.target.value as PartCondition)}>
                  {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="auth-field">
                <span className="auth-label">Status</span>
                <select value={form.status} onChange={(e) => update('status', e.target.value as PartStatus)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <small className="field-help">
                  Draft = hidden. Live = on the catalog. Reserved = visible, flagged. Sold = hidden.
                </small>
              </label>
            </div>

            <div className="auth-field" style={{ marginTop: 6 }}>
              <span className="auth-label">Part Icon</span>
              <small className="field-help" style={{ marginBottom: 8 }}>Shows on the listing card when there are no photos.</small>
              <IconPicker value={form.iconId} onChange={(iid) => update('iconId', iid)} />
            </div>
          </div>

          {/* ---------- Pricing & Detail ---------- */}
          <div className="card-elev">
            <h2 className="h-section">Pricing & Detail</h2>
            <div className="form-row">
              <label className="auth-field">
                <span className="auth-label">Price (USD)</span>
                <input type="number" min="0" step="1" value={form.price} onChange={(e) => update('price', e.target.value)} placeholder="4800" required />
              </label>
              <label className="auth-field">
                <span className="auth-label">Weight (lbs)</span>
                <input type="number" min="0" step="0.5" value={form.weight} onChange={(e) => update('weight', e.target.value)} placeholder="64" required />
              </label>
              <label className="auth-field">
                <span className="auth-label">Stock</span>
                <input type="number" min="0" step="1" value={form.stock} onChange={(e) => update('stock', e.target.value)} required />
              </label>
            </div>
            <div className="form-row">
              <label className="auth-field">
                <span className="auth-label">Casting / Part Number</span>
                <input value={form.casting} onChange={(e) => update('casting', e.target.value)} placeholder="2806178" />
              </label>
              <label className="auth-field">
                <span className="auth-label">Date Code</span>
                <input value={form.dateCode} onChange={(e) => update('dateCode', e.target.value)} placeholder="0G27" />
              </label>
            </div>
            <label className="auth-field">
              <span className="auth-label">Vehicles This Fits</span>
              <textarea
                rows={4}
                value={form.fitment}
                onChange={(e) => update('fitment', e.target.value)}
                placeholder={'1970 Cuda Hemi\n1970 Challenger R/T Hemi\n1970 Coronet Super Bee Hemi'}
                style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
              />
              <small className="field-help">One vehicle per line.</small>
            </label>
            <label className="auth-field">
              <span className="auth-label">Description</span>
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="What it is, what's included, condition specifics, anything a buyer should know."
                style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
              />
            </label>
          </div>

          {/* ---------- Authentication ---------- */}
          <div className="card-elev">
            <h2 className="h-section">Authentication</h2>
            <p style={{ fontSize: 12.5, color: 'var(--text-dim)', marginBottom: 14, lineHeight: 1.55 }}>
              The verification record customers see on the part page. This is the SWC Promise in action — be specific.
            </p>
            <div className="form-row">
              <label className="auth-field">
                <span className="auth-label">Verified By</span>
                <input value={form.verifiedBy} onChange={(e) => update('verifiedBy', e.target.value)} />
              </label>
              <label className="auth-field">
                <span className="auth-label">Verified Date</span>
                <input type="date" value={form.verifiedDate} onChange={(e) => update('verifiedDate', e.target.value)} />
              </label>
            </div>
            <label className="auth-field">
              <span className="auth-label">Authentication Notes</span>
              <textarea
                rows={4}
                value={form.authNotes}
                onChange={(e) => update('authNotes', e.target.value)}
                placeholder="Casting numbers verified, date codes matched, paint traces, build sheet, anything that proves it's the real deal."
                style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
              />
            </label>
          </div>

          {/* ---------- Photos ---------- */}
          <div className="card-elev">
            <h2 className="h-section">Photos</h2>
            <p style={{ fontSize: 12.5, color: 'var(--text-dim)', marginBottom: 14, lineHeight: 1.55 }}>
              First photo is the main image on the listing card. Up to 10 MB per photo. JPEG, PNG, or HEIC.
            </p>
            {photos.length > 0 && (
              <div className="photo-grid" style={{ marginBottom: 14 }}>
                {photos.map((ph) => (
                  <div key={ph.storagePath} className="photo-thumb">
                    <img src={ph.url} alt={ph.alt} />
                    <button type="button" className="photo-remove" onClick={() => handleRemovePhoto(ph)} title="Remove">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="btn dark sm" style={{ cursor: 'pointer' }}>
              {uploading ? 'Uploading…' : '＋ Add Photo'}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handlePhotoUpload(f);
                  e.target.value = '';
                }}
                disabled={uploading}
              />
            </label>
            {photos.length === 0 && (
              <div style={{ marginTop: 14, padding: 14, background: 'var(--bg)', border: '1px dashed var(--line-2)', fontSize: 12, color: 'var(--text-muted)' }}>
                With no photos, the listing falls back to this icon:
                <div style={{ marginTop: 10, color: 'var(--rust)' }}>
                  <PartIcon iconId={form.iconId} size={64} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="part-form-actions">
          <button type="button" className="btn dark" onClick={() => navigate('/admin/inventory')} disabled={saving}>Cancel</button>
          <button type="submit" className="btn" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Part →'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
